export const professionalServices = [
  {
    slug: "civil-engineer",
    title: "Civil Engineer",
    shortTitle: "Civil Engineer",
    path: "/civil-engineer",
    category: "Professional & Project Services",
    badge: "Execution planning",
    icon: "CE",
    overview:
      "Plan structural execution, stage-wise site decisions, and practical construction guidance with engineer-led support for residential and small commercial work.",
    bookingModes: [
      "Site visit booking",
      "Consultation booking",
      "Project request",
      "Inspection booking",
    ],
    useCases: [
      "Plot planning before starting foundation work",
      "Structural review before slab, beam, or column decisions",
      "Site issue diagnosis for cracks, seepage, or execution quality",
      "Bill and milestone review before contractor payments",
    ],
    trustIndicators: [
      "Verification-ready engineer onboarding",
      "Project-scope-aware consultation flow",
      "Closure amount captured before completion",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Experience checked",
      "Identity and document review",
    ],
    pricing: {
      model: "consultation_range",
      marketRateMin: 999,
      marketRateMax: 2999,
      rateUnit: "visit",
      pricingModel: "Consultation and inspection",
      startingPriceLabel: "Engineer consultation from Rs 999",
    },
    deliverables: [
      "Site observations",
      "Execution recommendations",
      "Material and method notes",
      "Optional follow-up visit planning",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Pune", "Chennai", "Remote review"],
    portfolioHighlights: [
      "Independent house supervision",
      "Renovation structural checks",
      "Execution audit support",
    ],
    faqs: [
      {
        question: "Is this for full project execution or only consultation?",
        answer:
          "It supports both short consultation and broader project execution coordination, depending on scope and booking type.",
      },
      {
        question: "Will final pricing be fixed online?",
        answer:
          "No. Final pricing can vary with scope, urgency, complexity, location, and professional availability.",
      },
    ],
  },
  {
    slug: "architect",
    title: "Architect",
    shortTitle: "Architect",
    path: "/architect",
    category: "Professional & Project Services",
    badge: "Design + approvals",
    icon: "AR",
    overview:
      "Get design-led planning, concept support, layout thinking, and coordination-ready architectural guidance for new builds, renovations, and extensions.",
    bookingModes: ["Design consultation", "Site visit booking", "Project request"],
    useCases: [
      "House plan consultation before build",
      "Layout redesign for renovation or extension",
      "Facade and space planning decisions",
      "Coordination between owner, engineer, and contractor",
    ],
    trustIndicators: [
      "Scope-first booking intake",
      "Portfolio-led decision support",
      "OTP-backed start and completion workflow",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Portfolio reviewed",
      "Document verification ready",
    ],
    pricing: {
      model: "consultation_range",
      marketRateMin: 1499,
      marketRateMax: 4999,
      rateUnit: "consultation",
      pricingModel: "Consultation and design scoping",
      startingPriceLabel: "Architect consultation from Rs 1,499",
    },
    deliverables: [
      "Initial design discussion",
      "Requirement capture",
      "Space planning notes",
      "Next-step recommendation",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Mumbai", "Delhi NCR", "Remote planning"],
    portfolioHighlights: [
      "2BHK and villa planning",
      "Renovation layouts",
      "Elevation concept direction",
    ],
    faqs: [
      {
        question: "Can I book only for discussion before full design work?",
        answer:
          "Yes. Consultation and scoped project requests are supported separately so customers can start small before committing.",
      },
      {
        question: "Are approvals guaranteed?",
        answer:
          "No. The platform can support planning and lawful documentation coordination, but it must not imply guaranteed approvals.",
      },
    ],
  },
  {
    slug: "interior-designer",
    title: "Interior Designer",
    shortTitle: "Interior Designer",
    path: "/interior-designer",
    category: "Professional & Project Services",
    badge: "Finish and fit-out",
    icon: "ID",
    overview:
      "Plan interiors, storage, finishes, and execution priorities with a designer-friendly workflow built for realistic budgets and staged delivery.",
    bookingModes: ["Design consultation", "Site visit booking", "Project request"],
    useCases: [
      "End-to-end interior planning for apartments and villas",
      "Modular kitchen and storage layout decisions",
      "Room-by-room renovation prioritization",
      "Material and finish selection guidance",
    ],
    trustIndicators: [
      "Project value capture before completion",
      "Portfolio-first customer trust layer",
      "Scope mismatch review support",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Portfolio reviewed",
      "Response-time monitored",
    ],
    pricing: {
      model: "project_range",
      marketRateMin: 50000,
      marketRateMax: 500000,
      rateUnit: "project",
      pricingModel: "Project-based design and execution",
      startingPriceLabel: "Interior projects usually start from Rs 50,000",
    },
    deliverables: [
      "Budget-aligned concept direction",
      "Room scope planning",
      "Execution sequencing",
      "Material and finish recommendations",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Pune", "Chennai", "Remote concept support"],
    portfolioHighlights: [
      "Apartment interiors",
      "Villa fit-outs",
      "Kitchen and wardrobe packages",
    ],
    faqs: [
      {
        question: "Why is there a wide price range?",
        answer:
          "Interior work changes significantly with area, materials, carpentry scope, finishes, and execution style, so the marketplace captures a range first.",
      },
      {
        question: "Can MistriBazaar track final project value?",
        answer:
          "Yes. Professionals must declare final project value before completion confirmation so closure and future commission rules remain measurable.",
      },
    ],
  },
  {
    slug: "site-supervisor-project-manager",
    title: "Site Supervisor / Project Manager",
    shortTitle: "Site Supervisor",
    path: "/site-supervisor-project-manager",
    category: "Professional & Project Services",
    badge: "Recurring supervision",
    icon: "PM",
    overview:
      "Coordinate daily site execution, labor movement, contractor sequencing, and issue follow-ups with recurring supervision workflows.",
    bookingModes: ["Recurring supervision", "Site visit booking", "Project request"],
    useCases: [
      "Daily or weekly supervision for active sites",
      "Contractor coordination when owner is remote",
      "Progress tracking against planned milestones",
      "Quality and escalation reporting",
    ],
    trustIndicators: [
      "Recurring supervision booking mode",
      "Completion percentage tracking",
      "Project closure controls before job completion",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Experience checked",
      "Operational availability reviewed",
    ],
    pricing: {
      model: "monthly_range",
      marketRateMin: 20000,
      marketRateMax: 60000,
      rateUnit: "month",
      pricingModel: "Recurring supervision",
      startingPriceLabel: "Site supervision from Rs 20,000 per month",
    },
    deliverables: [
      "Progress updates",
      "Coordination notes",
      "Issue escalation summary",
      "Completion status tracking",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Pune", "Chennai"],
    portfolioHighlights: [
      "Owner-absent site supervision",
      "Renovation project coordination",
      "Vendor and labor follow-up support",
    ],
    faqs: [
      {
        question: "Can this be booked as a recurring service?",
        answer:
          "Yes. Recurring supervision is a first-class booking mode so longer-running site coordination can be tracked consistently.",
      },
      {
        question: "What does completion tracking record?",
        answer:
          "The system is designed to track work completed percentage, summary updates, closure amount, and any dispute before final completion.",
      },
    ],
  },
  {
    slug: "boq-quantity-estimator",
    title: "BOQ / Quantity Estimator",
    shortTitle: "BOQ Estimator",
    path: "/boq-quantity-estimator",
    category: "Professional & Project Services",
    badge: "Quantity control",
    icon: "BQ",
    overview:
      "Capture quantities, scope clarity, and cost visibility before execution so customers can compare, negotiate, and track project value more safely.",
    bookingModes: ["Consultation booking", "Project request", "Inspection booking"],
    useCases: [
      "BOQ creation before contractor onboarding",
      "Quantity checks for renovation or finishing packages",
      "Cross-checking vendor proposals",
      "Reducing scope leakage before payments",
    ],
    trustIndicators: [
      "Project-value intelligence workflow",
      "Quote and closure compatibility",
      "Scope documentation support",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Document workflow ready",
      "Experience checked",
    ],
    pricing: {
      model: "document_range",
      marketRateMin: 2999,
      marketRateMax: 15000,
      rateUnit: "assignment",
      pricingModel: "BOQ and estimation",
      startingPriceLabel: "BOQ estimation from Rs 2,999",
    },
    deliverables: [
      "Quantity estimate",
      "Scope summary",
      "Cost framing support",
      "Quote comparison notes",
    ],
    serviceAreas: ["India-wide remote support", "Hyderabad", "Bengaluru", "Pune"],
    portfolioHighlights: [
      "Residential BOQs",
      "Interior quantity sheets",
      "Execution-ready scope docs",
    ],
    faqs: [
      {
        question: "Does a BOQ fix my contractor price?",
        answer:
          "No. It improves visibility and comparability, but final execution value can still vary by market rate, materials, and scope changes.",
      },
      {
        question: "Why is this important for the platform?",
        answer:
          "BOQ and closure tracking help reduce hidden offline value leakage and make project economics more measurable over time.",
      },
    ],
  },
  {
    slug: "visualization-expert",
    title: "2D / 3D Visualization Expert",
    shortTitle: "2D / 3D Visualization",
    path: "/visualization-expert",
    category: "Professional & Project Services",
    badge: "Pre-visualization",
    icon: "3D",
    overview:
      "Visualize layouts, interiors, or elevations before execution with plan-to-render support that helps customers decide faster and reduce revision waste.",
    bookingModes: ["Design consultation", "Project request"],
    useCases: [
      "2D plan drafting",
      "3D room or facade visualization",
      "Interior concept preview before execution",
      "Client sign-off support for custom work",
    ],
    trustIndicators: [
      "Portfolio-led trust",
      "Revision and scope notes captured",
      "Closure amount and completion gating supported",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Portfolio reviewed",
      "Response-time monitored",
    ],
    pricing: {
      model: "asset_range",
      marketRateMin: 5000,
      marketRateMax: 50000,
      rateUnit: "assignment",
      pricingModel: "Drawing and visualization",
      startingPriceLabel: "2D or 3D work from Rs 5,000",
    },
    deliverables: [
      "2D drafts or markups",
      "3D renders or previews",
      "Revision notes",
      "Execution handoff references",
    ],
    serviceAreas: ["India-wide remote support", "Hyderabad", "Bengaluru"],
    portfolioHighlights: [
      "2D plans",
      "3D room renders",
      "Facade previews",
    ],
    faqs: [
      {
        question: "Can this be remote-only?",
        answer:
          "Yes. Many visualization requests can be remote, though some projects still need an on-site measurement visit first.",
      },
      {
        question: "Is revision scope captured?",
        answer:
          "The platform is being extended to keep project notes and closure summaries visible so rework and scope drift are easier to manage.",
      },
    ],
  },
  {
    slug: "approval-documentation-consultant",
    title: "Approval & Documentation Consultant",
    shortTitle: "Approval Consultant",
    path: "/approval-documentation-consultant",
    category: "Professional & Project Services",
    badge: "Lawful coordination",
    icon: "AD",
    overview:
      "Get lawful application support, document coordination, and process guidance for construction-related approvals and paperwork.",
    bookingModes: ["Consultation booking", "Project request"],
    useCases: [
      "Application document preparation support",
      "Plan and paperwork coordination",
      "Submission-readiness checks",
      "Owner guidance on process steps",
    ],
    trustIndicators: [
      "Lawful wording enforced",
      "No guaranteed approvals messaging",
      "Documentation support lane separated from execution",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Documentation workflow ready",
      "Identity reviewed",
    ],
    pricing: {
      model: "consultation_range",
      marketRateMin: 1999,
      marketRateMax: 25000,
      rateUnit: "assignment",
      pricingModel: "Application and documentation support",
      startingPriceLabel: "Documentation assistance from Rs 1,999",
    },
    deliverables: [
      "Checklist guidance",
      "Document readiness notes",
      "Submission support",
      "Coordination summary",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Pune", "Chennai"],
    portfolioHighlights: [
      "Documentation support",
      "Application readiness",
      "Coordination follow-through",
    ],
    faqs: [
      {
        question: "Do you guarantee approvals?",
        answer:
          "No. The service supports lawful documentation and coordination only. It must not imply guaranteed approvals or shortcuts.",
      },
      {
        question: "Can this be combined with architect or engineer help?",
        answer:
          "Yes. The system is being structured so professional categories can work together across the same project lifecycle.",
      },
    ],
  },
  {
    slug: "waterproofing-specialist",
    title: "Waterproofing Specialist",
    shortTitle: "Waterproofing Specialist",
    path: "/waterproofing-specialist",
    category: "Professional & Project Services",
    badge: "Leakage and treatment",
    icon: "WP",
    overview:
      "Handle seepage diagnosis, terrace treatment planning, wet-area correction, and execution-linked waterproofing support with clearer scope capture.",
    bookingModes: ["Inspection booking", "Site visit booking", "Project request"],
    useCases: [
      "Terrace seepage treatment",
      "Bathroom and wall dampness investigation",
      "Exterior leakage source review",
      "Patch repair before larger treatment",
    ],
    trustIndicators: [
      "Inspection-first for variable scope",
      "Final project value declaration before closure",
      "Dispute-ready mismatch handling",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Experience checked",
      "Work summary required on closure",
    ],
    pricing: {
      model: "area_range",
      marketRateMin: 40,
      marketRateMax: 150,
      rateUnit: "sq.ft",
      pricingModel: "Area-based treatment",
      startingPriceLabel: "Waterproofing rates commonly start around Rs 40 per sq.ft",
    },
    deliverables: [
      "Leak source diagnosis",
      "Treatment recommendation",
      "Area and scope capture",
      "Completion summary",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Pune", "Chennai"],
    portfolioHighlights: [
      "Terrace treatment",
      "Dampness correction",
      "Wet-area waterproofing",
    ],
    faqs: [
      {
        question: "Why is inspection so important here?",
        answer:
          "Waterproofing outcomes depend heavily on exact source, surface, area, and weather conditions, so pricing and scope often need a site diagnosis first.",
      },
      {
        question: "Can the worker close the job without sharing final amount?",
        answer:
          "No. The closure design requires final project value and work summary before completion confirmation.",
      },
    ],
  },
  {
    slug: "solar-installation-partner",
    title: "Solar Installation Partner",
    shortTitle: "Solar Installation",
    path: "/solar-installation-partner",
    category: "Professional & Project Services",
    badge: "Energy systems",
    icon: "SO",
    overview:
      "Assess rooftop solar viability, installation scope, and execution planning with a project-friendly intake that supports consultation and custom quotes.",
    bookingModes: ["Site visit booking", "Consultation booking", "Project request"],
    useCases: [
      "Rooftop solar viability check",
      "System sizing and budget planning",
      "Installation coordination support",
      "Upgrade or replacement discussion",
    ],
    trustIndicators: [
      "Custom quote ready lifecycle",
      "Project-value tracking for higher-ticket jobs",
      "OTP-backed execution checkpoints",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Experience checked",
      "Scope review ready",
    ],
    pricing: {
      model: "capacity_range",
      marketRateMin: 45000,
      marketRateMax: 75000,
      rateUnit: "kW",
      pricingModel: "Capacity-based installation",
      startingPriceLabel: "Solar installation often ranges from Rs 45,000 to Rs 75,000 per kW",
    },
    deliverables: [
      "Site viability notes",
      "System sizing guidance",
      "Custom quote capture",
      "Execution summary",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Pune", "Chennai"],
    portfolioHighlights: [
      "Residential rooftop systems",
      "Small commercial setups",
      "Upgrade consultations",
    ],
    faqs: [
      {
        question: "Will the website show a fixed final solar price?",
        answer:
          "No. Solar pricing depends on capacity, hardware, roof condition, installation complexity, and location.",
      },
      {
        question: "Why track completion percentage for this category?",
        answer:
          "Longer-cycle projects benefit from milestone visibility and future payout logic, even before escrow is introduced.",
      },
    ],
  },
  {
    slug: "smart-home-automation-expert",
    title: "CCTV / Smart Home / Automation Expert",
    shortTitle: "Smart Home Expert",
    path: "/smart-home-automation-expert",
    category: "Professional & Project Services",
    badge: "Security and automation",
    icon: "SH",
    overview:
      "Plan CCTV, smart locks, sensors, automation controls, and home-tech execution with a category that supports both consultation and installation requests.",
    bookingModes: ["Consultation booking", "Site visit booking", "Project request"],
    useCases: [
      "CCTV planning for homes and shops",
      "Smart lock and access setup",
      "Automation planning for lights and appliances",
      "Security and device upgrade advice",
    ],
    trustIndicators: [
      "Consultation to execution continuity",
      "Closure tracking for custom package value",
      "Verification and response badges",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Portfolio reviewed",
      "Identity reviewed",
    ],
    pricing: {
      model: "package_range",
      marketRateMin: 1500,
      marketRateMax: 10000,
      rateUnit: "package",
      pricingModel: "Consultation or device package",
      startingPriceLabel: "CCTV and smart home support starts from Rs 1,500",
    },
    deliverables: [
      "Coverage and layout advice",
      "Device recommendation notes",
      "Installation scope summary",
      "Completion confirmation support",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Mumbai", "Delhi NCR"],
    portfolioHighlights: [
      "CCTV home packages",
      "Smart lock upgrades",
      "Basic home automation setup",
    ],
    faqs: [
      {
        question: "Is this different from the existing CCTV technician service?",
        answer:
          "Yes. The platform keeps technician-style jobs and project-oriented consultation in one broader service architecture without duplicating the execution lane.",
      },
      {
        question: "Can the final package amount be revised?",
        answer:
          "Yes, but revisions should be declared in the closure workflow and can be flagged if they look suspicious.",
      },
    ],
  },
  {
    slug: "rental-equipment-provider",
    title: "Rental Equipment Provider",
    shortTitle: "Rental Equipment",
    path: "/rental-equipment-provider",
    category: "Professional & Project Services",
    badge: "Equipment logistics",
    icon: "RE",
    overview:
      "Book project equipment with daily pricing, availability, operator-required flags, and transport notes so temporary site needs stay organized.",
    bookingModes: ["Rental booking", "Site visit booking", "Project request"],
    useCases: [
      "Scaffolding requirement",
      "Mixer, cutter, drill, or welding machine rental",
      "Short-duration site equipment needs",
      "Operator-backed equipment requests",
    ],
    trustIndicators: [
      "Inventory and condition placeholders",
      "Transport-note aware booking flow",
      "Availability captured in structured data",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Inventory reviewed",
      "Operator requirement flagged",
    ],
    pricing: {
      model: "daily_rental",
      marketRateMin: 500,
      marketRateMax: 5000,
      rateUnit: "day",
      pricingModel: "Daily rental pricing model",
      startingPriceLabel: "Daily rental pricing varies by equipment type and duration",
    },
    deliverables: [
      "Availability confirmation",
      "Transport notes",
      "Operator requirement capture",
      "Closure and return summary",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Pune", "Chennai"],
    portfolioHighlights: [
      "Scaffolding rentals",
      "Machine rentals",
      "Temporary site equipment",
    ],
    faqs: [
      {
        question: "Does every rental include an operator?",
        answer:
          "No. Operator requirement is a structured flag so the platform can distinguish self-use rentals from operator-backed supply.",
      },
      {
        question: "How is pricing shown?",
        answer:
          "Rental pricing should stay backend-driven and equipment-specific, so the customer sees ranges and daily pricing guidance first.",
      },
    ],
  },
  {
    slug: "government-liaison-utility-agent",
    title: "Government Liaison & Utility Connection Agent",
    shortTitle: "Government Liaison",
    path: "/government-liaison-utility-agent",
    category: "Professional & Project Services",
    badge: "Lawful coordination",
    icon: "GL",
    overview:
      "Coordinate lawful documentation assistance, application support, and utility connection process guidance without implying shortcuts or guaranteed outcomes.",
    bookingModes: ["Consultation booking", "Project request"],
    useCases: [
      "Electricity board coordination support",
      "Water board or drainage paperwork support",
      "Application process guidance",
      "Documentation readiness for lawful submissions",
    ],
    trustIndicators: [
      "No shortcut-approval language",
      "Lawful coordination wording only",
      "Documentation and closure tracking support",
    ],
    verificationBadges: [
      "MistriBazaar Verified",
      "Identity reviewed",
      "Documentation workflow ready",
    ],
    pricing: {
      model: "assistance_range",
      marketRateMin: 1999,
      marketRateMax: 25000,
      rateUnit: "assignment",
      pricingModel: "Lawful coordination and application support",
      startingPriceLabel: "Lawful liaison support from Rs 1,999",
    },
    deliverables: [
      "Process guidance",
      "Document coordination",
      "Application follow-up summary",
      "Scope and closure record",
    ],
    serviceAreas: ["Hyderabad", "Bengaluru", "Pune", "Chennai"],
    portfolioHighlights: [
      "Utility application support",
      "Documentation assistance",
      "Lawful coordination follow-up",
    ],
    faqs: [
      {
        question: "Does this service use inside contacts or shortcuts?",
        answer:
          "No. The category is limited to lawful documentation assistance, liaison support, and application coordination.",
      },
      {
        question: "Why capture final project value here too?",
        answer:
          "Even consultation-led categories can suffer offline closure leakage, so the platform should still measure declared value and completion.",
      },
    ],
  },
];

export const professionalServiceMap = professionalServices.reduce(
  (acc, service) => ({
    ...acc,
    [service.slug]: service,
  }),
  {}
);

export const professionalServiceIndexEntries = professionalServices.map((service) => ({
  title: service.title,
  icon: service.icon,
  path: service.path,
  category: service.category,
  tags: service.bookingModes.map((mode) => mode.toLowerCase()),
}));

export const pricingDisclaimer =
  "Final pricing may vary depending on project scope, materials, urgency, complexity, location, and professional availability.";
